import type { QueryClient } from '@tanstack/react-query';
import type { EntityTag } from '@signalvote/shared';

/**
 * The freshest token this client has shown or produced for each row. A row's version only rises
 * on the server, so the store keeps the highest one: a stale row passed in after a newer write
 * cannot pull it back. A 409's current version never enters it, because the client has not seen
 * the change that version stands for.
 */
export interface VersionStore {
  get(entity: EntityTag, id: string): number | undefined;
  /**
   * Record a token the client holds: a row a caller passed to a write, or a token its own
   * successful write returned. Ignored when an equal or newer one is already held.
   */
  observe(entity: EntityTag, id: string, version: number): void;
}

/** A place in one row's queue: `turn` settles once every earlier place has been released. */
export interface RowTurn {
  readonly turn: Promise<void>;
  /** Give the place up. Safe to call more than once. */
  release(): void;
}

export interface RowLedger extends VersionStore {
  /** Take the row's next place in the queue now, before any work that precedes the write. */
  reserve(entity: EntityTag, id: string): RowTurn;
  /** Take the next place in a write's serial lane. */
  lane(key: string): RowTurn;
  /** Record the row a successful write returned. */
  wrote(entity: EntityTag, id: string, row: { concurrencyVersion: number }): void;
  /**
   * The freshest state of `row` this client holds: `row` with the fields of the last row a write
   * returned laid over it, when that write is at least as new as `row`. Every write to an entity
   * returns the entity's row, so a field the two share means the same thing in both.
   */
  freshest<T extends { concurrencyVersion: number }>(entity: EntityTag, id: string, row: T): T;
}

const ledgers = new WeakMap<QueryClient, RowLedger>();

function createRowLedger(): RowLedger {
  const versions = new Map<string, number>();
  const tails = new Map<string, Promise<void>>();
  const written = new Map<string, { concurrencyVersion: number }>();
  function reserveKey(key: string): RowTurn {
    const turn = tails.get(key) ?? Promise.resolve();
    let release!: () => void;
    const released = new Promise<void>((resolve) => {
      release = resolve;
    });
    const tail = turn.then(() => released);
    tails.set(key, tail);
    void tail.then(() => {
      if (tails.get(key) === tail) tails.delete(key);
    });
    return { turn, release };
  }
  return {
    get: (entity, id) => versions.get(`${entity}:${id}`),
    observe(entity, id, version) {
      const key = `${entity}:${id}`;
      if (!(version <= (versions.get(key) ?? -Infinity))) versions.set(key, version);
    },
    reserve: (entity, id) => reserveKey(`${entity}:${id}`),
    lane: (key) => reserveKey(`lane:${key}`),
    wrote(entity, id, row) {
      written.set(`${entity}:${id}`, row);
    },
    freshest(entity, id, row) {
      const last = written.get(`${entity}:${id}`);
      if (!last || last.concurrencyVersion < row.concurrencyVersion) return row;
      const fresh = { ...row };
      for (const key of Object.keys(row) as (keyof typeof row)[]) {
        if (key in last) fresh[key] = (last as typeof row)[key];
      }
      return fresh;
    },
  };
}

/** The row ledger for one query client: one per app, and a fresh one per test client. */
export function rowLedger(queryClient: QueryClient): RowLedger {
  let ledger = ledgers.get(queryClient);
  if (!ledger) {
    ledger = createRowLedger();
    ledgers.set(queryClient, ledger);
  }
  return ledger;
}

/** The version store a query client's row writes read their tokens from. */
export function versionStore(queryClient: QueryClient): VersionStore {
  return rowLedger(queryClient);
}
